import { useCallback, useRef } from 'react'

import { AbsolutePopup, TPopupVertical } from 'core-components/library/popup'

import { PopupContent } from './components/popupContent/PopupContent'
import { SelectToggle } from './components/selectToggle/SelectToggle'
import { useEscapeKeyPress } from './hooks/useEscapeKeyPress'
import { usePopup } from './hooks/usePopup'
import { useTreeSelect } from './hooks/useTreeSelect'
import { IDataShape } from './models/dataShape'
import { ITreeItemClickFn, ITreeSelectProps } from './models/treeSelectProps'

import styles from './TreeSelect.module.scss'

export const TreeSelect = <T extends IDataShape>({
  values,
  data,
  itemCaption,
  label,
  popupClassName,
  onChange,
  onSearch,
  disabled,
  typeOfSelectedNodes = 'allSelectedItems',
}: ITreeSelectProps<T>) => {
  /** -------------------- Variables ----------------------- */
  const { isOpened, setOpened } = usePopup()
  const listRef = useRef<HTMLUListElement>(null)

  const {
    treeData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
    selectItem,
    removeItem,
    removeAll,
  } = useTreeSelect({
    data,
    values,
    itemCaption,
    onChange,
    onSearch,
    typeOfSelectedNodes,
  })

  /** -------------------- Methods ----------------------- */
  const handleItemClick = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const isChecked = selectedValues.some((it) => it.itemId === itemId)
      if (isChecked) {
        removeItem(itemId)
      } else {
        selectItem(itemId)
      }
    },
    [selectedValues, selectItem, removeItem]
  )

  const handleClosePopup = useCallback(() => {
    setOpened(false)
  }, [setOpened])

  /** -------------------- Hooks ----------------------- */

  useEscapeKeyPress({ cb: handleClosePopup, condition: isOpened })

  return (
    <div className={styles.root}>
      <AbsolutePopup
        popupClassName={popupClassName}
        verticalStartPosition={TPopupVertical.Bottom}
        visibilityCallback={(newValue: boolean) => {
          setOpened(newValue)
          return newValue
        }}
        popupContent={() => {
          return (
            <PopupContent
              isOpened={isOpened}
              showOnlySelected={showOnlySelected}
              searchValue={searchValue}
              treeData={treeData}
              selectedValues={selectedValues}
              listRef={listRef}
              setSearchValue={setSearchValue}
              setShowOnlySelected={setShowOnlySelected}
              handleItemClick={handleItemClick}
              onBlur={handleClosePopup}
            />
          )
        }}
      >
        <SelectToggle
          label={label}
          selectedValues={selectedValues}
          onRemoveItem={removeItem}
          onRemoveAll={removeAll}
          disabled={disabled}
          isOpened={isOpened}
        />
      </AbsolutePopup>
    </div>
  )
}
